import { Navigate, RouteObject } from "react-router-dom";
import { FavouriteDrivers, FavouriteGuests, FavouriteLocations, NotFound, RouteError } from "@/components/pages";
import { JoinOperator } from "@/components/pages";
import {
  AssistantClientBooking,
  AssistantClientBookingConfirmation,
  ClientBooking,
  ClientBookingConfirmation,
  ClientFavourites,
  ClientHelp,
  ClientHome,
  ClientMyProfile,
  ClientReferrals,
  ClientSettings,
  ManageClientDetails,
  ManageClientFavourites,
  ManageClientSettings,
  ManageClientTakeover,
  MyClients,
} from "@/components/pages/Client";
import { SettingsAssistants, SettingsEmailNotifications, SettingsPayments, SettingsProfile } from "@/components/pages/Client/settings";
import { ClientPortal } from "@/containers";
import { RequireAuth } from "./RequireAuth/RequireAuth";
import { RequireClientAccount } from "./RequireClientAccount/RequireClientAccount";

export const clientRoutes: RouteObject = {
  path: "client",
  element: <RequireAuth />,
  errorElement: <RouteError />,
  children: [
    {
      path: "",
      element: <ClientPortal />,
      children: [
        // Recovery flow: registration is complete but the user has no active operator (detached from
        // a lapsed/suspended one). Sits OUTSIDE RequireClientAccount and renders full-screen.
        { path: "join-operator", element: <JoinOperator /> },
        {
          path: "",
          element: <RequireClientAccount />,
          children: [
            { index: true, element: <ClientHome /> },
            { path: "booking", element: <ClientBooking /> },
            { path: "booking/confirmation", element: <ClientBookingConfirmation /> },
            // One URL for an existing booking: the sheet decides view vs edit from the booking's own
            // `is_editable` flag (editable → editable sheet + "Update Booking"; otherwise read-only).
            { path: "booking/:jobId", element: <ClientBooking /> },
            // Back-compat: the old explicit /edit link now just resolves to the same URL.
            { path: "booking/:jobId/edit", element: <Navigate to=".." relative="path" replace /> },
            // EA books on behalf of a CONNECTED managed client (DEV-1296). Standalone routes under
            // the normal portal chrome (slim banner, NOT the Manage-Client takeover), keyed on the
            // access-link id — mirrors the operator OBO booking routes in routes/operators.tsx.
            { path: "book-for/:accessId", element: <AssistantClientBooking /> },
            { path: "book-for/:accessId/confirmation", element: <AssistantClientBookingConfirmation /> },
            // View/edit an existing scoped booking — the sheet resolves view vs edit from `is_editable`.
            { path: "book-for/:accessId/:jobId", element: <AssistantClientBooking /> },
            {
              path: "favourites",
              element: <ClientFavourites />,
              children: [
                { index: true, element: <Navigate to="locations" replace /> },
                { path: "locations", element: <FavouriteLocations /> },
                { path: "guests", element: <FavouriteGuests /> },
                { path: "drivers", element: <FavouriteDrivers /> },
              ],
            },
            { path: "referrals", element: <ClientReferrals /> },
            { path: "my-profile", element: <ClientMyProfile /> },
            { path: "my-clients", element: <MyClients /> },
            // "Manage Client" full-screen takeover (DEV-408): the EA acts on behalf of a connected
            // client. The shell resolves :accessId → account uuid and scopes every reused screen via
            // ClientScopeProvider. Renders bare (no ClientHeader) — see ClientPortal chrome-skip.
            {
              path: "my-clients/:accessId/manage",
              element: <ManageClientTakeover />,
              children: [
                { index: true, element: <Navigate to="details" replace /> },
                { path: "details", element: <ManageClientDetails /> },
                {
                  path: "favourites",
                  element: <ManageClientFavourites />,
                  children: [
                    { index: true, element: <Navigate to="locations" replace /> },
                    { path: "locations", element: <FavouriteLocations /> },
                    { path: "guests", element: <FavouriteGuests /> },
                    { path: "drivers", element: <FavouriteDrivers /> },
                  ],
                },
                {
                  path: "settings",
                  element: <ManageClientSettings />,
                  children: [
                    { index: true, element: <Navigate to="profile" replace /> },
                    { path: "profile", element: <SettingsProfile /> },
                    { path: "notifications", element: <SettingsEmailNotifications /> },
                    { path: "payments", element: <SettingsPayments /> },
                  ],
                },
              ],
            },
            {
              path: "settings",
              element: <ClientSettings />,
              children: [
                { index: true, element: <Navigate to="profile" replace /> },
                { path: "profile", element: <SettingsProfile /> },
                { path: "notifications", element: <SettingsEmailNotifications /> },
                { path: "assistants", element: <SettingsAssistants /> },
                { path: "payments", element: <SettingsPayments /> },
              ],
            },
            { path: "help", element: <ClientHelp /> },
            { path: "*", element: <NotFound /> },
          ],
        },
      ],
    },
  ],
};
